import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { ConfigProvider } from '@/contexts/ConfigContext';
import { RoleProvider } from '@/contexts/RoleContext';
import { AuthProvider } from '@/hooks/useAuth';
import { Layout } from '@/components/Layout';
import { AdminLayout } from '@/components/AdminLayout';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { AdminRoute } from '@/components/AdminRoute';

// Pages
import { Home } from '@/pages/Home';
import { Items } from '@/pages/Items';
import { ItemDetail } from '@/pages/ItemDetail';
import { NewItem } from '@/pages/NewItem';
import { EditItem } from '@/pages/EditItem';
import { EveryonesItems } from '@/pages/EveryonesItems';
import { ProductRoadmap } from '@/pages/ProductRoadmap';
import { Login } from '@/pages/Login';
import { AuthCallback } from '@/pages/AuthCallback';
import { Logout } from '@/pages/Logout';

// Admin Pages
import { AdminUsers } from '@/pages/admin/AdminUsers';
import { AdminConfiguration } from '@/pages/admin/AdminConfiguration';
import { AdminIntegrations } from '@/pages/admin/AdminIntegrations';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <ConfigProvider>
          <RoleProvider>
            <AuthProvider>
              <BrowserRouter>
                <Routes>
                  {/* Public routes */}
                  <Route path="/login" element={<Login />} />
                  <Route path="/logout" element={<Logout />} />
                  <Route path="/auth/callback" element={<AuthCallback />} />

                  {/* Protected routes - Main Layout */}
                  <Route
                    path="/"
                    element={
                      <ProtectedRoute>
                        <Layout />
                      </ProtectedRoute>
                    }
                  >
                    <Route index element={<Home />} />

                    {/* Your Items */}
                    <Route path="items" element={<Items />} />
                    <Route path="items/new" element={<NewItem />} />
                    <Route path="items/:id" element={<ItemDetail />} />
                    <Route path="items/:id/edit" element={<EditItem />} />

                    {/* Everyone's Stuff */}
                    <Route path="all-items" element={<EveryonesItems />} />
                    <Route path="roadmap" element={<ProductRoadmap />} />
                  </Route>

                  {/* Admin routes - Admin Layout */}
                  <Route
                    path="/admin"
                    element={
                      <ProtectedRoute>
                        <AdminRoute>
                          <AdminLayout />
                        </AdminRoute>
                      </ProtectedRoute>
                    }
                  >
                    {/* Default redirect to users */}
                    <Route index element={<Navigate to="/admin/users" replace />} />
                    <Route path="users" element={<AdminUsers />} />
                    <Route path="integrations" element={<AdminIntegrations />} />
                    <Route path="configuration" element={<AdminConfiguration />} />
                  </Route>

                  {/* Catch all */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </BrowserRouter>
              <Toaster
                position="top-right"
                toastOptions={{
                  className: 'dark:bg-gray-800 dark:text-white',
                  duration: 4000,
                }}
              />
            </AuthProvider>
          </RoleProvider>
        </ConfigProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
